import { Check, ExternalLink, GitBranch, MapPin, Search, Sparkles, Store, Tag, X, Zap } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import { searchEstablishmentsAdmin, bulkUpdateEstablishmentTags, getEstablishmentTags } from "../../../interfaces/establishments";
import { IEstablishment } from "../../../interfaces/establishment";

interface TagInputProps {
    label: string;
    value: string[];
    suggestions: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    color: "blue" | "purple";
}

function TagInput({ label, value, suggestions, onChange, placeholder, color }: TagInputProps) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const filtered = query.length > 0
        ? suggestions.filter(s => s.toLowerCase().includes(query.toLowerCase()) && !value.includes(s))
        : suggestions.filter(s => !value.includes(s)).slice(0, 8);

    const add = (tag: string) => {
        const trimmed = tag.trim();
        if (trimmed && !value.includes(trimmed)) onChange([...value, trimmed]);
        setQuery("");
        inputRef.current?.focus();
    };

    const remove = (tag: string) => onChange(value.filter(t => t !== tag));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === "Enter" || e.key === ",") && query.trim()) { e.preventDefault(); add(query); }
        if (e.key === "Backspace" && !query && value.length > 0) remove(value[value.length - 1]);
        if (e.key === "Escape") setOpen(false);
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const tagColors = color === "blue" ? "bg-gray-100 text-gray-700 border-gray-200" : "bg-purple-50 text-purple-700 border-purple-200";
    const dotColor = color === "blue" ? "bg-gray-100" : "bg-purple-500";

    return (
        <div ref={containerRef} className="relative">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                {label}
            </p>
            <div
                className="min-h-[36px] flex flex-wrap gap-1.5 items-center border border-gray-200 rounded-sm p-1.5 cursor-text bg-white focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-200 transition-all"
                onClick={() => { inputRef.current?.focus(); setOpen(true); }}
            >
                {value.map(tag => (
                    <span key={tag} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium border ${tagColors}`}>
                        {tag}
                        <button type="button" onClick={e => { e.stopPropagation(); remove(tag); }} className="hover:opacity-70">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={value.length === 0 ? placeholder : ""}
                    className="flex-1 min-w-[100px] border-0 outline-none focus:ring-0 text-xs text-gray-700 placeholder:text-gray-400 bg-transparent"
                />
            </div>
            {open && filtered.length > 0 && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-sm max-h-48 overflow-y-auto">
                    {filtered.map(s => (
                        <button key={s} type="button" onMouseDown={e => { e.preventDefault(); add(s); }}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2">
                            <Tag className="w-3 h-3 text-gray-400 shrink-0" />{s}
                        </button>
                    ))}
                    {query.trim() && !suggestions.includes(query.trim()) && (
                        <button type="button" onMouseDown={e => { e.preventDefault(); add(query); }}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2 border-t border-gray-100">
                            <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
                            Create &ldquo;<strong>{query.trim()}</strong>&rdquo;
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function ExistingTagBadge({ tags, color }: { tags: string[]; color: "blue" | "purple" }) {
    if (tags.length === 0) return <span className="text-[10px] text-gray-300 italic">—</span>;
    const colors = color === "blue" ? "bg-gray-100 text-gray-700 border-gray-200" : "bg-purple-50 text-purple-600 border-purple-100";
    return (
        <div className="flex flex-wrap gap-1">
            {tags.map(t => (
                <span key={t} className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium border ${colors}`}>{t}</span>
            ))}
        </div>
    );
}

export default function BulkTag() {
    const [query, setQuery] = useState("");
    const [noFranchise, setNoFranchise] = useState(false);
    const [page, setPage] = useState(1);
    const [results, setResults] = useState<IEstablishment[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [selected, setSelected] = useState<Set<string>>(new Set());

    const [categories, setCategories] = useState<string[]>([]);
    const [subCategories, setSubCategories] = useState<string[]>([]);
    const [allCategories, setAllCategories] = useState<string[]>([]);
    const [allSubCategories, setAllSubCategories] = useState<string[]>([]);

    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState<number | null>(null);

    const LIMIT = 25;

    const fetchResults = useCallback(async (q: string, pg: number, nf: boolean) => {
        setLoading(true);
        setHasSearched(true);
        try {
            const data = await searchEstablishmentsAdmin({ q, page: pg, limit: LIMIT, noFranchise: nf });
            setResults(data.establishments);
            setTotal(data.total);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error("Search failed", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSelected(new Set());
            fetchResults(query, 1, noFranchise);
        }, 350);
        return () => clearTimeout(timer);
    }, [query, noFranchise, fetchResults]);

    // Load tag suggestions
    useEffect(() => {
        getEstablishmentTags().then(tags => {
            setAllCategories(tags.categories);
            setAllSubCategories(tags.subCategories);
        }).catch(console.error);
        // Trigger initial fetch
        fetchResults("", 1, false);
    }, [fetchResults]);

    const allPageIds = results.map(r => r._id);
    const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selected.has(id));

    const toggleSelectAll = () => {
        if (allPageSelected) {
            setSelected(prev => {
                const next = new Set(prev);
                allPageIds.forEach(id => next.delete(id));
                return next;
            });
        } else {
            setSelected(prev => new Set([...prev, ...allPageIds]));
        }
    };

    const toggleOne = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handlePageChange = (pg: number) => {
        setPage(pg);
        fetchResults(query, pg, noFranchise);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleApply = async () => {
        if (selected.size === 0 || (categories.length === 0 && subCategories.length === 0)) return;
        setApplying(true);
        setApplyResult(null);
        try {
            const result = await bulkUpdateEstablishmentTags([...selected], { categories, subCategories });
            setApplyResult(result.modifiedCount);
            setSelected(new Set());
            // Refresh results to show updated tags
            fetchResults(query, page, noFranchise);
            // Refresh tag suggestions
            getEstablishmentTags().then(tags => {
                setAllCategories(tags.categories);
                setAllSubCategories(tags.subCategories);
            });
        } catch (err) {
            console.error("Bulk apply failed", err);
        } finally {
            setApplying(false);
        }
    };

    const canApply = selected.size > 0 && (categories.length > 0 || subCategories.length > 0);

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Bulk Tag</h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Recherchez des établissements par mot-clé et tagguez-les en masse
                    </p>
                </div>

                <BONavigation />

                <div className="flex gap-6">
                    {/* Left: Search + Results */}
                    <div className="flex-1 min-w-0">
                        {/* Search bar + filters */}
                        <div className="flex gap-2 mb-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Rechercher par nom: pizza, sushi, burger..."
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-sm text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-200"
                                />
                                {query && (
                                    <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => setNoFranchise(v => !v)}
                                className={`flex items-center gap-1.5 px-3 py-2 border rounded-sm text-xs font-medium transition-colors shrink-0 ${
                                    noFranchise
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                                }`}
                            >
                                <GitBranch className="w-3.5 h-3.5" />
                                Sans franchise
                            </button>
                        </div>

                        {/* Results table */}
                        <div className="border border-gray-200 rounded-sm overflow-hidden">
                            {/* Table header */}
                            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
                                <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    onChange={toggleSelectAll}
                                    className="w-3.5 h-3.5 rounded border-gray-300 accent-black cursor-pointer"
                                />
                                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex-1">
                                    {loading ? "Chargement..." : hasSearched ? `${total.toLocaleString("fr-FR")} établissement${total > 1 ? "s" : ""}` : ""}
                                </span>
                                {selected.size > 0 && (
                                    <span className="text-[11px] font-semibold text-black bg-black/5 px-2 py-0.5 rounded-sm">
                                        {selected.size} sélectionné{selected.size > 1 ? "s" : ""}
                                    </span>
                                )}
                                <span className="text-[11px] text-gray-400 hidden sm:block w-24 text-center">Catégories</span>
                                <span className="text-[11px] text-gray-400 hidden sm:block w-28 text-center">Sous-catégories</span>
                            </div>

                            {loading ? (
                                <div className="divide-y divide-gray-100">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="flex items-center gap-3 px-3 py-3 animate-pulse">
                                            <div className="w-3.5 h-3.5 bg-gray-100 rounded shrink-0" />
                                            <div className="w-8 h-8 bg-gray-100 rounded-sm shrink-0" />
                                            <div className="flex-1 space-y-1.5">
                                                <div className="h-3.5 bg-gray-100 rounded w-2/3" />
                                                <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : results.length === 0 ? (
                                <div className="py-16 text-center">
                                    <Store className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                    <p className="text-sm text-gray-400">
                                        {hasSearched && query ? `Aucun résultat pour "${query}"` : "Aucun établissement"}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {results.map(est => {
                                        const city = est.address?.city;
                                        const postcode = est.address?.postcode;
                                        const location = [postcode, city].filter(Boolean).join(" ");
                                        const isSelected = selected.has(est._id);
                                        const mapsUrl = est.ratings?.source_summary?.find((s: any) => s.source === "google")?.url ?? null;
                                        const siren = est.registration?.siren;
                                        const pappersUrl = siren ? `https://www.pappers.fr/entreprise/${siren}` : null;

                                        return (
                                            <div
                                                key={est._id}
                                                onClick={() => toggleOne(est._id)}
                                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isSelected ? "bg-black/[0.03]" : "hover:bg-[#F9F6F3]/60"}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleOne(est._id)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-3.5 h-3.5 rounded border-gray-300 accent-black cursor-pointer shrink-0"
                                                />
                                                <div className="w-8 h-8 rounded-sm bg-gray-100 flex items-center justify-center shrink-0">
                                                    <Store className="w-4 h-4 text-gray-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <p className="text-xs font-semibold text-gray-900 truncate">{est.name}</p>
                                                        {est.franchise?.name && (
                                                            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded-sm bg-amber-50 text-[10px] text-amber-700 border border-amber-200 shrink-0">
                                                                <GitBranch className="w-2.5 h-2.5" />{est.franchise.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        {location && (
                                                            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                                                                <MapPin className="w-2.5 h-2.5" />{location}
                                                            </span>
                                                        )}
                                                        {est.type && (
                                                            <span className="px-1 py-0.5 rounded-sm bg-gray-100 text-[10px] text-gray-500 border border-gray-200">{est.type}</span>
                                                        )}
                                                        {est.brand_group && (
                                                            <span className="px-1 py-0.5 rounded-sm bg-gray-100 text-[10px] text-gray-700 border border-gray-200">{est.brand_group}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="hidden sm:block w-24 shrink-0">
                                                    <ExistingTagBadge tags={est.categories || []} color="blue" />
                                                </div>
                                                <div className="hidden sm:block w-28 shrink-0">
                                                    <ExistingTagBadge tags={est.subCategories || []} color="purple" />
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                                    {mapsUrl && (
                                                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                                            title="Google Maps"
                                                            className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                                                            <MapPin className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    {pappersUrl && (
                                                        <a href={pappersUrl} target="_blank" rel="noopener noreferrer"
                                                            title="Pappers"
                                                            className="p-1 rounded text-gray-300 hover:text-gray-700 hover:bg-[#333] transition-colors">
                                                            <ExternalLink className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-4">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Précédent
                                </button>
                                <span className="text-xs text-gray-500">Page {page} / {totalPages}</span>
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right: Apply panel */}
                    <div className="w-72 shrink-0">
                        <div className="sticky top-4 border border-gray-200 rounded-sm p-4 bg-white">
                            <div className="flex items-center gap-2 mb-4">
                                <Zap className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-semibold text-gray-900">Appliquer des tags</span>
                            </div>

                            {/* Selection summary */}
                            <div className={`mb-4 p-2.5 rounded-sm text-xs ${selected.size > 0 ? "bg-black text-white" : "bg-gray-50 text-gray-500 border border-gray-200"}`}>
                                {selected.size > 0 ? (
                                    <span className="font-semibold">{selected.size} établissement{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}</span>
                                ) : (
                                    "Sélectionnez des établissements dans la liste"
                                )}
                            </div>

                            {/* Tag inputs */}
                            <div className="space-y-3 mb-4">
                                <TagInput
                                    label="Catégories"
                                    value={categories}
                                    suggestions={allCategories}
                                    onChange={setCategories}
                                    placeholder="Ex: Restaurant, Bar..."
                                    color="blue"
                                />
                                <TagInput
                                    label="Sous-catégories"
                                    value={subCategories}
                                    suggestions={allSubCategories}
                                    onChange={setSubCategories}
                                    placeholder="Ex: Japonais, Pizza..."
                                    color="purple"
                                />
                            </div>

                            {/* Warning */}
                            {categories.length > 0 || subCategories.length > 0 ? (
                                <p className="text-[10px] text-gray-400 mb-3">
                                    Les tags existants seront remplacés sur les établissements sélectionnés.
                                </p>
                            ) : null}

                            {/* Apply button */}
                            <button
                                onClick={handleApply}
                                disabled={!canApply || applying}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold transition-all ${
                                    canApply
                                        ? "bg-black text-white hover:bg-gray-800"
                                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                            >
                                {applying ? (
                                    <>
                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Application...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-3.5 h-3.5" />
                                        {selected.size > 0
                                            ? `Appliquer à ${selected.size} établissement${selected.size > 1 ? "s" : ""}`
                                            : "Appliquer les tags"
                                        }
                                    </>
                                )}
                            </button>

                            {/* Success feedback */}
                            {applyResult !== null && (
                                <div className="mt-3 flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-sm">
                                    <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                                    <p className="text-xs text-green-700 font-medium">
                                        {applyResult} établissement{applyResult > 1 ? "s" : ""} tagué{applyResult > 1 ? "s" : ""}
                                    </p>
                                    <button onClick={() => setApplyResult(null)} className="ml-auto text-green-500 hover:text-green-700">
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
